import { useState } from 'preact/hooks'
import type { ContactDetailDto } from '@/api/contactDetail'
import { leadsApi } from '@/api/leads'
import { Select, Checkbox } from '@/components'
import { useLookups } from '@/hooks/useLookups'
import { useToast } from '@/hooks/useToast'
import { handleApiError } from '@/utils/errorHandling'

/** What the Overview tab must supply for the Web source rows to become editable. */
export interface WebSourceEditContext {
  workspaceId: string
  campaignId: string
  webId: string
  onPatch: (patch: Partial<ContactDetailDto>) => void
}

interface Props extends WebSourceEditContext {
  detail: ContactDetailDto
}

/**
 * Auto-saving editor for a lead's per-Web attribution (ORB-608). It lived on LeadSidePanel until that
 * was retired in ORB-383 and was never re-homed, which left the Overview tab's Web source rows
 * read-only. Web scope only: the attribution is stored per Web, so there is no single value to edit at
 * campaign or workspace scope — which is also why the server only populates WebSource when given a webId.
 */
export function WebSourceEditor({ detail, workspaceId, campaignId, webId, onPatch }: Props) {
  const { getUtmSources } = useLookups()
  const { showSuccess, showError } = useToast()
  const [saving, setSaving] = useState(false)

  const webSource = detail.WebSource
  const leadId = detail.LeadId
  if (!webSource || !leadId) return null

  const save = async (updates: { Source?: string; HasAcceptedOffer?: boolean }) => {
    if (saving) return
    setSaving(true)
    const next = {
      Source: updates.Source ?? webSource.Source ?? '',
      HasAcceptedOffer: updates.HasAcceptedOffer ?? webSource.HasAcceptedOffer,
    }
    const res = await leadsApi.updateWebSourceAttribution(workspaceId, campaignId, leadId, webId, {
      ETag: detail.ETag || '',
      ...next,
    })
    setSaving(false)
    if (res.error || !res.data) {
      // handleApiError swallows 422 and 403 — GlobalErrorHandler already toasts those, and for 403 it
      // also refreshes the profile, so a second toast here would just talk over the better message.
      handleApiError(res, showError, () => {}, 'Attribution not saved')
      return
    }
    // The PATCH bumps the lead's ETag. Carry the new one back onto the page state, or a second edit
    // in the same visit (source then checkbox) sends the stale ETag and is rejected as a conflict.
    onPatch({ ETag: res.data.ETag, WebSource: { ...webSource, ...next } })
    showSuccess('Attribution updated', 'Web source attribution has been saved')
  }

  // Both the option values and the stored Source are the enum's [Description] labels ("OrbitalX
  // Calling"), not the identifiers — the API serializes enums that way.
  const options = getUtmSources().map(s => ({ value: s.Description, label: s.Description }))

  return (
    <div class="space-y-3">
      <Select
        name="attributionSource"
        label="Source"
        density="compact"
        options={options}
        value={webSource.Source ?? ''}
        disabled={saving}
        className="disabled:opacity-60 disabled:cursor-not-allowed"
        placeholder="Select a source"
        onChange={(e: Event) => save({ Source: (e.target as HTMLSelectElement).value })}
      />
      <Checkbox
        name="hasAcceptedOffer"
        label="Offer accepted"
        checked={webSource.HasAcceptedOffer}
        disabled={saving}
        onChange={(e: Event) => save({ HasAcceptedOffer: (e.target as HTMLInputElement).checked })}
      />
    </div>
  )
}
